import React, { useEffect, useState, useContext, useCallback } from 'react';
import api, { BACKEND_URL } from '../services/api';
import { AuthContext } from '../context/AuthContext';
import { 
  Plus, 
  X, 
  Calendar as CalendarIcon, 
  User, 
  LayoutGrid,
  Trash2,
  Mail
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Tasks = () => {
  const { user } = useContext(AuthContext);
  const [tasks, setTasks] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', assigned_to: '', due_date: '' });
  
  // Calendar States

  const fetchTasks = useCallback(async () => {
    try {
      const res = await api.get('/tasks');
      setTasks(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    const fetchStaff = async () => {
      if (user?.role !== 'Admin') return;
      try {
        const res = await api.get('/users');
        setStaffList(res.data);
      } catch (err) {
        console.error('Error fetching staff:', err);
      }
    };
    fetchStaff();
  }, [user, fetchTasks]);



  const [selectedTask, setSelectedTask] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/tasks', formData);
      fetchTasks();
      setShowForm(false);
      setFormData({ title: '', description: '', assigned_to: '', due_date: '' });
    } catch (err) {
      alert('Error creating task');
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.put(`/tasks/${id}/status`, { status });
      fetchTasks();
    } catch (err) {
      alert('Error updating status');
    }
  };



  const handleDelete = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await api.delete(`/tasks/${id}`);
      fetchTasks();
    } catch (err) {
      alert('Error deleting task');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-[80vh]">
      <div className="w-12 h-12 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
    </div>
  );

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-8 animate-in fade-in duration-700">
      
      {/* Premium Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div>
          <h1 className="text-4xl font-black text-gray-900 tracking-tighter">Task List</h1>
          <p className="text-sm font-black text-indigo-400 uppercase tracking-[0.3em] mt-2 flex items-center gap-2">
            <LayoutGrid size={14} /> Task Management
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {user?.role === 'Admin' && (
            <button 
              onClick={() => setShowForm(!showForm)}
              className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-black uppercase tracking-widest transition-all shadow-xl ${
                showForm ? 'bg-rose-50 text-rose-600' : 'bg-indigo-600 text-white shadow-indigo-100'
              }`}
            >
              {showForm ? <X size={20} /> : <Plus size={20} />}
              {showForm ? 'Cancel' : 'New Task'}
            </button>
          )}
        </div>
      </div>



      {showForm && (
        <div className="bg-white p-8 rounded-[2.5rem] border-2 border-dashed border-indigo-200 animate-in slide-in-from-top-4 duration-300">
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2 space-y-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Task Title</label>
                <input type="text" className="w-full px-6 py-4 bg-gray-50 rounded-2xl border-none outline-none font-black text-gray-900 focus:ring-2 focus:ring-indigo-600 transition-all" placeholder="Enter task title..." value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Description</label>
                <textarea className="w-full px-6 py-4 bg-gray-50 rounded-2xl border-none outline-none font-medium text-gray-700 focus:ring-2 focus:ring-indigo-600 transition-all" rows="3" placeholder="Provide details..." value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} />
              </div>
            </div>
            <div className="space-y-6">
               <div className="space-y-2">
                 <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Assignee</label>
                 <select className="w-full px-6 py-4 bg-gray-50 rounded-2xl border-none outline-none font-black text-gray-900 focus:ring-2 focus:ring-indigo-600 appearance-none" value={formData.assigned_to} onChange={e => setFormData({...formData, assigned_to: e.target.value})} required>
                   <option value="">Select Staff</option>
                   {staffList.map(s => <option key={s.id} value={s.id}>{s.full_name || s.username}</option>)}
                 </select>
               </div>
               <div className="space-y-2">
                 <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Deadline Date</label>
                 <input type="date" className="w-full px-6 py-4 bg-gray-50 rounded-2xl border-none outline-none font-black text-gray-900 focus:ring-2 focus:ring-indigo-600 transition-all" value={formData.due_date} onChange={e => setFormData({...formData, due_date: e.target.value})} />
               </div>
               <button type="submit" className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-black uppercase tracking-widest shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95">Add Task</button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {['pending', 'in-progress', 'completed'].map((status) => (
          <div key={status} className="bg-gray-50/50 p-6 rounded-[3rem] border border-gray-100 min-h-[500px] space-y-6">
            <div className="flex items-center justify-between px-4">
              <h2 className="text-sm font-black text-gray-900 uppercase tracking-widest">{status}</h2>
              <div className={`w-6 h-6 rounded-lg flex items-center justify-center text-[10px] font-black ${status === 'completed' ? 'bg-emerald-500 text-white' : 'bg-white text-gray-400 border border-gray-100'}`}>
                {tasks.filter(t => t.status === status).length}
              </div>
            </div>

            <div className="space-y-4">
              {tasks.filter(t => t.status === status).map(task => (
                <motion.div key={task.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-white p-6 rounded-[2rem] shadow-sm border border-transparent hover:border-indigo-100 transition-all group hover:shadow-xl hover:shadow-indigo-900/5 cursor-pointer" onClick={() => setSelectedTask(task)}>
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-sm font-black text-gray-900 group-hover:text-indigo-600 transition-colors leading-tight">{task.title}</h3>
                    <div className="w-8 h-8 rounded-xl bg-gray-50 border border-gray-100 overflow-hidden flex items-center justify-center shrink-0">
                      {task.assigned_photo ? (
                        <img 
                          src={task.assigned_photo.startsWith('http') ? task.assigned_photo : `${BACKEND_URL}${task.assigned_photo}`} 
                          alt={task.assigned_name || 'Assignee'}
                          className="w-full h-full object-cover" 
                        />
                      ) : (
                        <User size={14} className="text-gray-300" />
                      )}
                    </div>
                  </div>
                  <p className="text-[11px] font-medium text-gray-400 line-clamp-2 mb-6">{task.description}</p>
                  
                  <div className="flex justify-between items-center pt-4 border-t border-gray-50">
                     <div className="flex items-center gap-1.5">
                        <CalendarIcon size={12} className="text-indigo-400" />
                        <span className="text-[10px] font-black text-gray-900">{task.due_date ? new Date(task.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'ASAP'}</span>
                     </div>
                     <div className="flex items-center gap-2">
                       <select className={`text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-lg border appearance-none text-center cursor-pointer ${status === 'completed' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : status === 'in-progress' ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-gray-50 text-gray-500 border-gray-100'}`} value={task.status} onChange={e => handleStatusChange(task.id, e.target.value)}>
                          <option value="pending">Pending</option>
                          <option value="in-progress">Active</option>
                          <option value="completed">Done</option>
                       </select>
                       {user?.role === 'Admin' && (
                         <button onClick={() => handleDelete(task.id)} className="p-1.5 bg-rose-50 text-rose-500 rounded-lg hover:bg-rose-500 hover:text-white transition-all" title="Delete Task">
                           <Trash2 size={14} />
                         </button>
                       )}
                     </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Task Detail Modal */}
      <AnimatePresence>
        {selectedTask && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setSelectedTask(null)} className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" />
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="relative w-full max-w-md bg-white rounded-[2.5rem] shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="p-8 pb-6 border-b border-gray-50 flex justify-between items-start">
                <div className="flex-1 pr-4">
                  <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-2">Task Details</p>
                  <h2 className="text-xl font-black text-gray-900 leading-tight">{selectedTask.title}</h2>
                </div>
                <button onClick={() => setSelectedTask(null)} className="p-2 hover:bg-gray-50 rounded-xl transition-all shrink-0"><X size={20} className="text-gray-400" /></button>
              </div>

              <div className="p-8 space-y-6">
                {/* Assigned To */}
                <div className="bg-indigo-50 rounded-2xl p-5 flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-white border border-indigo-100 overflow-hidden flex items-center justify-center shrink-0 shadow-sm">
                    {selectedTask.assigned_photo ? (
                      <img src={selectedTask.assigned_photo.startsWith('http') ? selectedTask.assigned_photo : `${BACKEND_URL}${selectedTask.assigned_photo}`} alt={selectedTask.assigned_name} className="w-full h-full object-cover" />
                    ) : (
                      <User size={24} className="text-indigo-300" />
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Assigned To</p>
                    <p className="text-base font-black text-indigo-900 mt-0.5">{selectedTask.assigned_name || selectedTask.assigned_user || 'Unassigned'}</p>
                    {selectedTask.assigned_user && <p className="text-xs font-bold text-indigo-500">@{selectedTask.assigned_user}</p>}
                  </div>
                </div>

                {/* Description */}
                {selectedTask.description && (
                  <div>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Description</p>
                    <p className="text-sm font-medium text-gray-700 leading-relaxed bg-gray-50 rounded-xl p-4">{selectedTask.description}</p>
                  </div>
                )}

                {/* Meta */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Deadline</p>
                    <div className="flex items-center gap-1.5">
                      <CalendarIcon size={14} className="text-indigo-400" />
                      <p className="text-sm font-black text-gray-900">{selectedTask.due_date ? new Date(selectedTask.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'ASAP'}</p>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Status</p>
                    <span className={`text-xs font-black uppercase px-2 py-1 rounded-lg ${
                      selectedTask.status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
                      selectedTask.status === 'in-progress' ? 'bg-amber-100 text-amber-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>{selectedTask.status}</span>
                  </div>
                </div>

                {/* Created by */}
                {selectedTask.creator_name && (
                  <div className="flex items-center gap-2 text-xs font-bold text-gray-400">
                    <Mail size={13} />
                    <span>Created by <span className="text-gray-700">{selectedTask.creator_name}</span></span>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Tasks;
